import React, { useState } from 'react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { useAuth } from '../contexts/AuthContext';
import { Mail, LogIn } from 'lucide-react';
import heroBackground from '../assets/images/hero-background.png';
import procogiaLogo from '../assets/images/procogia-logo-light.png';

const SocialLoginPage = () => {
  const { loginWithGoogle, loginWithLinkedIn, loginWithEmail, auth0IsLoading } = useAuth0Context();
  const { login } = useAuth(); // Traditional login fallback
  const [showTraditionalLogin, setShowTraditionalLogin] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTraditionalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await login(credentials.email, credentials.password);
      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showTraditionalLogin) {
    return (
      <div 
        className="min-h-screen bg-white relative"
        style={{
          backgroundImage: `url(${heroBackground}), url('https://procogia.com/wp-content/uploads/2025/04/hero-background.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Background overlay for better text readability */}
        <div className="absolute inset-0 bg-black/20"></div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center">
              <div className="flex items-center justify-center mb-6">
                <img
                  src={procogiaLogo}
                  alt="ProCogia Logo"
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.src = "https://procogia.com/wp-content/uploads/2024/03/procogia-horizontal-light-bg-1-2048x487.png";
                  }}
                />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">Predictive Maintenance Platform</h1>
              <p className="text-gray-600 mb-8">Advanced AI-Powered Manufacturing Intelligence</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleTraditionalLogin} className="space-y-4">
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email Address"
                  required
                />
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Password"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                                          className="w-full text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                        style={{
                          backgroundColor: '#95c100',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#1fb8cc'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#95c100'}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <LogIn className="h-5 w-5" />
                      <span>Sign In</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6">
                <button
                  onClick={() => setShowTraditionalLogin(false)}
                  className="font-medium text-sm transition-colors"
                  style={{ color: '#1fb8cc' }}
                  onMouseEnter={(e) => e.target.style.color = '#000000'}
                  onMouseLeave={(e) => e.target.style.color = '#1fb8cc'}
                >
                  ← Back to Social Login
                </button>
              </div>

              <div className="mt-6 pt-6" style={{ borderTop: '1px solid #c9cace' }}>
                <p className="text-xs mb-2" style={{ color: '#65676e' }}>Demo Credentials:</p>
                <div className="text-xs space-y-1" style={{ color: '#333333' }}>
                  <div>ops@company.com / ops123</div>
                  <div>admin@company.com / admin123</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-white relative"
      style={{
        backgroundImage: `url(${heroBackground}), url('https://procogia.com/wp-content/uploads/2025/04/hero-background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center">
            {/* ProCogia Logo */}
            <div className="flex items-center justify-center mb-6">
              <img
                src={procogiaLogo}
                alt="ProCogia Logo"
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.src = "https://procogia.com/wp-content/uploads/2024/03/procogia-horizontal-light-bg-1-2048x487.png";
                }}
              />
            </div>

            <h1 className="text-2xl font-bold mb-2" style={{ color: '#333333' }}>Predictive Maintenance Platform</h1>
            <p className="mb-8" style={{ color: '#333333' }}>Advanced AI-Powered Manufacturing Intelligence</p>
            {/* Cache bust: 2025-07-27-18:06 */}

            <p className="text-sm mb-8 leading-relaxed" style={{ color: '#333333' }}>
              Access your comprehensive manufacturing dashboard with AI-powered predictive analytics, real-time monitoring, and intelligent maintenance automation.
            </p>

            {/* Primary CTA Button */}
            <button
              onClick={loginWithGoogle}
              disabled={auth0IsLoading}
              className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 mb-6 flex items-center justify-center space-x-2"
              style={{
                backgroundColor: '#95c100',
                color: '#ffffff',
                border: 'none'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#000000'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#95c100'}
            >
              {auth0IsLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>🔐</span>
                  <span>Sign In</span>
                </>
              )}
            </button>



            {/* Authentication Options */}
            <div className="pt-6 border-t" style={{ borderColor: '#c9cace' }}>
              <p className="text-sm font-medium mb-2" style={{ color: '#333333' }}>Secure Authentication Options</p>
              <p className="text-xs" style={{ color: '#65676e' }}>🌐 Google OAuth • 💼 LinkedIn • 📧 Enterprise SSO</p>
              <p className="text-xs mt-2 italic" style={{ color: '#65676e' }}>Enterprise-grade security with multi-factor authentication</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialLoginPage; 